import { Loader2 } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ReviewRequestItem } from "@/features/review-request/components/review-request-item";
import { ReviewStateArray } from "@/constants";
import { historyQueryOptions } from "@/lib/client/review";
import type { CurrentUser } from "@/lib/client/user";
import type { QueryClient } from "@tanstack/react-query";
import z from "zod";

export const historySearchSchema = z.object({
  tab: z.enum(ReviewStateArray).optional().catch(undefined),
});

export type HistorySearchParams = z.infer<typeof historySearchSchema>;

type ReviewHistoryPageProps = {
  queryClient: QueryClient;
  search: HistorySearchParams;
  onTabChange: (tab: HistorySearchParams["tab"]) => void;
  user: CurrentUser;
};

const filterTabs: { value: "all" | (typeof ReviewStateArray)[number]; label: string }[] = [
  {
    label: "全部",
    value: "all",
  },
  {
    label: "排队中",
    value: "queued",
  },
  {
    label: "已取消",
    value: "cancelled",
  },
  {
    label: "复盘中",
    value: "reviewing",
  },
  {
    label: "已被拒绝",
    value: "rejected",
  },
  {
    label: "已复盘",
    value: "reviewed",
  },
];

export function ReviewHistoryPage({
  queryClient,
  search,
  onTabChange,
  user,
}: ReviewHistoryPageProps) {
  const {
    data: historyPages,
    isFetching,
    hasNextPage,
    refetch,
    fetchNextPage,
  } = useSuspenseInfiniteQuery(historyQueryOptions(search));

  const histories = useMemo(() => {
    return historyPages?.pages.flatMap((page) => page.requests) ?? [];
  }, [historyPages]);

  const reload = useCallback(async () => {
    queryClient.setQueryData(historyQueryOptions(search).queryKey, (oldData) => {
      if (!oldData) {
        return undefined;
      }
      return {
        pages: oldData.pages.slice(0, 1),
        pageParams: oldData.pageParams.slice(0, 1),
      };
    });
    await queryClient.invalidateQueries(historyQueryOptions(search));
  }, [search, queryClient]);

  return (
    <div className="space-y-4">
      <Tabs
        className="sticky top-20 z-20 pb-2"
        value={search.tab ?? "all"}
        onValueChange={(value) =>
          onTabChange(value === "all" ? undefined : (value as HistorySearchParams["tab"]))
        }
      >
        <TabsList className="grid w-full grid-cols-6">
          {filterTabs.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <ul className="space-y-4">
        {(histories ?? []).map((item) => (
          <li key={item.id}>
            <ReviewRequestItem
              request={item}
              queuePosition={item.queuePosition}
              name={user.name}
              email={user.email ?? null}
              userActions
              clampDescription
              onModified={() => refetch()}
            />
          </li>
        ))}

        {!histories?.length && (
          <li>
            <p className="text-center text-muted-foreground my-12 text-sm">暂无数据</p>
          </li>
        )}

        {hasNextPage ? (
          <li>
            <Button className="mx-auto block" onClick={() => fetchNextPage()}>
              加载更多
            </Button>
          </li>
        ) : (
          <li>
            <p className="text-center text-muted-foreground my-12 text-sm">
              暂无更多数据
              <Button
                className="h-auto p-0 text-primary ml-2"
                onClick={() => reload()}
                variant="link"
              >
                刷新
              </Button>
            </p>
          </li>
        )}

        {isFetching && (
          <li>
            <Loader2 className="my-12 mx-auto animate-spin text-muted-foreground" />
          </li>
        )}
      </ul>
    </div>
  );
}
